/**
 * Вкладка «Премия» карточки сделки: кому и за что начислена премия по сделке,
 * сколько списано, итог. Считает CRM (крон a.reserv `userBonus.php`), здесь —
 * только показ. Спека `docs/specs/2026-09-25-deal-bonus-design.md`; те же
 * данные — `prokat-runner deal-bonus ID`.
 */
import { useQuery } from "@tanstack/react-query";
import { TriangleAlert } from "lucide-react";
import type { ReactNode } from "react";

import { type BonusLedger, type BonusPart, type BonusRow, type DealBonus, loadDealBonus } from "@/lib/dealBonus";

const PART: Record<BonusPart, string> = {
  product: "товары",
  second: "вторым получателем",
  payment: "за оплату",
  refuse: "бронь отказ",
  mentor: "наставник",
  other: "прочее",
};

const rub = (n: number) => `${n.toLocaleString("ru-RU")} ₽`;
const dt = (iso: string | null) => (iso ? `${iso.slice(8, 10)}.${iso.slice(5, 7)} ${iso.slice(11, 16)}` : "—");
const signed = (n: number) => (n > 0 ? "text-ok" : n < 0 ? "text-err" : "text-fg-muted");

function Stat({ label, value, hint, tone = "" }: { label: string; value: ReactNode; hint?: string; tone?: string }) {
  return (
    <div className="flex min-w-0 flex-col gap-1 rounded-[var(--radius-card)] border border-border bg-bg-subtle p-3" data-annot={`премия «${label}»`}>
      <div className="text-[12px] text-fg-muted">{label}</div>
      <div className={`text-[20px] font-semibold leading-none tabular-nums ${tone}`}>{value}</div>
      {hint && <div className="text-[11.5px] text-fg-faint">{hint}</div>}
    </div>
  );
}

/** База расчёта: для продаж и серий с учётом себестоимости — цена минус себестоимость. */
function Base({ r }: { r: BonusRow }) {
  const minus = r.price_cost ? r.price === r.price_account - r.price_cost : false;
  return (
    <span className="tabular-nums">
      {minus ? `${r.price_account} − ${r.price_cost} = ${r.price}` : r.price}
      {!minus && r.price_cost ? <span className="text-fg-faint" title="себестоимость в расчёте не вычитается"> (себ. {r.price_cost})</span> : null}
    </span>
  );
}

/** Журнал балансом: каждое начисление и списание строкой, остаток нарастающим итогом. */
function Ledger({ l, annot }: { l: BonusLedger; annot: string }) {
  if (!l.items.length) return <div className="px-3 py-2 text-[12.5px] text-fg-faint">проводок нет</div>;
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-[12.5px]" data-annot={annot}>
        <thead className="text-left text-[12px] text-fg-muted">
          <tr>
            <th className="px-3 py-1 font-normal">Дата</th>
            <th className="px-3 py-1 font-normal">Операция</th>
            <th className="px-3 py-1 font-normal">Кому</th>
            <th className="px-3 py-1 text-right font-normal">Начислено</th>
            <th className="px-3 py-1 text-right font-normal">Списано</th>
            <th className="px-3 py-1 text-right font-normal">Остаток</th>
          </tr>
        </thead>
        <tbody>
          {l.items.map((b) => (
            <tr key={b.id} className="border-t border-border align-top" data-annot={`проводка #${b.id}`}>
              <td className="whitespace-nowrap px-3 py-1 tabular-nums text-fg-muted">{dt(b.at)}</td>
              <td className="px-3 py-1">
                {b.module}
                {b.note && <span className="text-fg-muted"> · {b.note}</span>}
              </td>
              <td className="whitespace-nowrap px-3 py-1">{b.user ?? "—"}</td>
              <td className="px-3 py-1 text-right tabular-nums text-ok">{b.amount > 0 ? `+${b.amount}` : ""}</td>
              <td className="px-3 py-1 text-right tabular-nums text-err">{b.amount < 0 ? b.amount : ""}</td>
              <td className="px-3 py-1 text-right font-medium tabular-nums">{b.balance}</td>
            </tr>
          ))}
          <tr className="border-t border-border-strong font-medium">
            <td colSpan={3} className="px-3 py-1 text-fg-muted">Итого</td>
            <td className={`px-3 py-1 text-right tabular-nums ${signed(l.accrued)}`}>{l.accrued ? `+${l.accrued}` : "0"}</td>
            <td className={`px-3 py-1 text-right tabular-nums ${signed(l.written_off)}`}>{l.written_off || "0"}</td>
            <td className={`px-3 py-1 text-right tabular-nums ${signed(l.net)}`}>{l.net}</td>
          </tr>
        </tbody>
      </table>
    </div>
  );
}

/** Текущий расчёт премии по товару: база × процент = премия, кому. */
function Calc({ r }: { r: BonusRow | null }) {
  if (!r) return <div className="text-fg-muted">активной премии нет</div>;
  return (
    <div className="text-fg-muted">
      <Base r={r} /> × {r.percent}% <span className="text-fg-faint">{r.percent_type}</span> = <span className="font-medium text-fg">{r.bonus_sum}</span> → {r.user ?? "—"}
      {r.second_user && <> + {r.second_user} (вторым)</>}
      {r.archived && <span className="text-fg-faint"> · архив: {r.archive}</span>}
      {r.not_accrued.length > 0 && (
        <div className="mt-0.5 flex items-start gap-1 text-warn">
          <TriangleAlert size={12} className="mt-0.5 shrink-0" aria-hidden />
          <span>не начислено: {r.not_accrued.join("; ")}</span>
        </div>
      )}
    </div>
  );
}

function Product({ p }: { p: DealBonus["products"][number] }) {
  return (
    <div className="rounded-[var(--radius-card)] border border-border" data-annot={`премия серия ${p.serial}`}>
      <div className="flex items-start gap-3 bg-bg-subtle px-3 py-2 text-[12.5px]">
        {p.image ? (
          <a href={p.image} target="_blank" rel="noreferrer noopener" className="shrink-0">
            <img src={p.image} alt={`серия ${p.serial}`} loading="lazy" className="h-16 w-12 rounded-[var(--radius-ui)] border border-border bg-bg object-cover" />
          </a>
        ) : (
          <div className="h-16 w-12 shrink-0 rounded-[var(--radius-ui)] border border-dashed border-border" aria-hidden />
        )}
        <div className="min-w-0 flex-1">
          <div>
            <span className="font-mono font-medium">{p.serial}</span> {p.name && <span className="font-medium">{p.name}</span>}
            {p.is_sale && <span className="ml-2 text-[11.5px] text-accent">продажа</span>}
            {p.deleted && <span className="ml-2 text-[11.5px] text-err">удалён из договора</span>}
          </div>
          <Calc r={p.active} />
        </div>
        <div className={`shrink-0 text-right text-[15px] font-semibold tabular-nums ${signed(p.ledger.net)}`}>{rub(p.ledger.net)}</div>
      </div>
      <Ledger l={p.ledger} annot={`журнал серии ${p.serial}`} />
    </div>
  );
}

/** Сверка баланса: остатки товаров и «без товара» складываются в итог сверху. */
function Check({ b }: { b: DealBonus }) {
  const blocks = [...b.products.map((p) => p.ledger), b.other];
  const net = blocks.reduce((s, l) => s + l.net, 0);
  const off = blocks.reduce((s, l) => s + l.written_off, 0);
  const ok = Math.abs(net - b.total.net) < 0.01 && Math.abs(off - b.total.written_off) < 0.01;
  return (
    <div className={`text-[12.5px] ${ok ? "text-fg-muted" : "text-err"}`} data-annot="сверка премии">
      Сверка: остатки по блокам {rub(net)} {ok ? "=" : "≠"} итого {rub(b.total.net)} · списано в блоках {rub(off)}
    </div>
  );
}

function Body({ b }: { b: DealBonus }) {
  const t = b.total;
  return (
    <>
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
        <Stat label="Итого премия" value={rub(t.net)} hint="начислено минус списано" />
        <Stat label="Начислено" value={rub(t.accrued)} tone="text-ok" />
        <Stat label="Списано" value={rub(t.written_off)} tone={t.written_off ? "text-err" : ""} hint="откаты при пересчёте и отменах" />
        <Stat label="Ждёт начисления" value={rub(t.pending)} tone={t.pending ? "text-warn" : ""} hint="активные премии без проводки" />
      </div>
      {b.deal && (
        <div className="text-[12.5px] text-fg-muted">
          Стадия {b.deal.stage} · оплачено {rub(b.deal.paid)} из {rub(b.deal.product_sum)} товаров
        </div>
      )}

      <section className="flex flex-col gap-1.5" data-annot="премия по сотрудникам">
        <h2 className="text-[13px] font-medium">По сотрудникам</h2>
        {b.by_user.length ? (
          <table className="w-full max-w-2xl rounded-[var(--radius-card)] border border-border text-[12.5px]">
            <tbody>
              {b.by_user.map((u) => (
                <tr key={u.user_id} className="border-t border-border first:border-t-0">
                  <td className="px-3 py-1.5">{u.name ?? "—"}</td>
                  <td className="px-3 py-1.5 text-fg-muted">
                    {(Object.keys(PART) as BonusPart[])
                      .filter((k) => u.parts[k])
                      .map((k) => `${PART[k]} ${u.parts[k]}`)
                      .join(" · ") || "0"}
                  </td>
                  <td className={`px-3 py-1.5 text-right font-medium tabular-nums ${signed(u.net)}`}>{rub(u.net)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <div className="text-[12.5px] text-fg-muted">Проводок премии по сделке нет.</div>
        )}
      </section>

      <section className="flex flex-col gap-2" data-annot="премия по товарам">
        <h2 className="text-[13px] font-medium">По товарам — начисления и списания</h2>
        {b.products.length ? (
          b.products.map((p) => <Product key={p.serial} p={p} />)
        ) : (
          <div className="text-[12.5px] text-fg-muted">Премий по товарам нет.</div>
        )}
      </section>

      {b.other.items.length > 0 && (
        <section className="flex flex-col gap-1.5" data-annot="премия без товара">
          <h2 className="text-[13px] font-medium">Без товара</h2>
          <div className="rounded-[var(--radius-card)] border border-border">
            <Ledger l={b.other} annot="журнал без товара" />
          </div>
        </section>
      )}

      <Check b={b} />
    </>
  );
}

export function DealBonusTab({ dealId }: { dealId: number }) {
  const q = useQuery({ queryKey: ["deal-bonus", dealId], queryFn: () => loadDealBonus(dealId) });
  if (q.isPending) return <div className="text-[12.5px] text-fg-muted">Считаю премию из CRM…</div>;
  if (q.isError) return <div className="text-[12.5px] text-err">Не удалось получить премию: {String(q.error.message)}</div>;
  return <Body b={q.data} />;
}
